import { Alert, List, ListItem, SxProps, Theme, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Accept } from "react-dropzone";

import { GqlMedia } from "~/__generated__";

import useFileUploader from "../../hooks/useFileUploader";
import FileDropZone from "../FileDropZone/index.tsx";
import FileUploadItem from "../FileUploadItem.tsx";

type Props = {
    children?: React.ReactNode;
    onUploadFinished?: (uploadedMedia: GqlMedia[]) => void;
    multiple?: boolean;
    disabled?: boolean;
    fileTypesToAccept?: Accept;
    sx?: SxProps<Theme>;
};

export default function FileUploaderStatus({
    children,
    onUploadFinished,
    multiple = true,
    disabled = false,
    sx,
    fileTypesToAccept,
}: Props) {
    const { uploadItems, uploadFiles, reset, error: filedUploadError } = useFileUploader();
    const [processedUploads, setProcessedUploads] = useState<Set<string>>(new Set());

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            uploadFiles(acceptedFiles.map(f => ({ name: f.name, blob: f })));
        },
        [uploadFiles],
    );

    useEffect(() => {
        const finishedUploads = uploadItems.filter(item => item.type === "FinishedMedia");
        const finishedIds = finishedUploads.map(item => item.mediaItem.id);
        const newFinishedUploads = finishedUploads.filter(item => !processedUploads.has(item.mediaItem.id));

        if (onUploadFinished && newFinishedUploads.length > 0) {
            onUploadFinished(newFinishedUploads.map(item => item.mediaItem));

            setProcessedUploads(prev => new Set([...prev, ...finishedIds]));
        }
    }, [uploadItems, onUploadFinished, processedUploads]);

    useEffect(() => {
        if (filedUploadError) {
            reset();
        }

        if (uploadItems.length > 0 && uploadItems.every(item => item.type === "FinishedMedia")) {
            reset();
        }
    }, [uploadItems, reset, filedUploadError]);

    return (
        <FileDropZone
            sx={[{ p: 1, pt: 5 }, ...(Array.isArray(sx) ? sx : [sx])]}
            disabled={disabled}
            onReceivedFiles={onDrop}
            multiple={multiple}
            fileTypesToAccept={fileTypesToAccept}
        >
            {filedUploadError && (
                <Alert severity="error" sx={{ mb: 5 }}>
                    <Typography>{filedUploadError.message}</Typography>
                </Alert>
            )}

            {uploadItems && uploadItems.length > 0 && (
                <List>
                    {uploadItems.map(item => (
                        <ListItem key={item.id}>
                            <FileUploadItem item={item} />
                        </ListItem>
                    ))}
                </List>
            )}

            {children}
        </FileDropZone>
    );
}
